import {Component, EventEmitter, Input, Output} from '@angular/core';

/**
 * Reusable pagination control, styled to match the admin's orange theme.
 *
 *   <app-pagination
 *       [totalRecords]="totalRecords"
 *       [totalPages]="totalPages"
 *       [currentPage]="currentPage"
 *       (previousPage)="onPage($event)"
 *       (nextPage)="onPage($event)"
 *       (goToPage)="onPage($event)">
 *   </app-pagination>
 *
 * `currentPage` is controlled by the parent: each action emits the *target* page
 * number, and the parent updates `currentPage` (and refetches) in response.
 */
@Component({
    selector: 'app-pagination',
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss']
})
export class PaginationComponent {

    // Total number of records (shown in the summary label).
    @Input() totalRecords = 0;

    // Total number of pages to page through.
    @Input() totalPages = 0;

    // The currently active page (1-based).
    @Input() currentPage = 1;

    // How many numbered buttons to show around the current page before collapsing
    // the rest behind ellipses.
    @Input() maxButtons = 5;

    // Previous arrow clicked — emits the new (previous) page number.
    @Output() previousPage = new EventEmitter<number>();

    // Next arrow clicked — emits the new (next) page number.
    @Output() nextPage = new EventEmitter<number>();

    // A specific page number clicked — emits that page number.
    @Output() goToPage = new EventEmitter<number>();

    get hasPrev(): boolean {
        return this.currentPage > 1;
    }

    get hasNext(): boolean {
        return this.currentPage < this.totalPages;
    }

    // The windowed list of page numbers to render. `-1` marks an ellipsis gap.
    get pages(): number[] {
        const total = this.totalPages;
        if (!total || total < 1) {
            return [];
        }
        const max = Math.max(this.maxButtons, 1);
        if (total <= max) {
            return Array.from({length: total}, (_, i) => i + 1);
        }

        const half = Math.floor(max / 2);
        let start = this.currentPage - half;
        let end = this.currentPage + half;

        if (start < 1) {
            start = 1;
            end = max;
        }
        if (end > total) {
            end = total;
            start = total - max + 1;
        }

        const result: number[] = [];
        if (start > 1) {
            result.push(1);
            if (start > 2) {
                result.push(-1);
            }
        }
        for (let p = start; p <= end; p++) {
            result.push(p);
        }
        if (end < total) {
            if (end < total - 1) {
                result.push(-1);
            }
            result.push(total);
        }
        return result;
    }

    select(page: number) {
        if (page < 1 || page > this.totalPages || page === this.currentPage) {
            return;
        }
        this.goToPage.emit(page);
    }

    prev() {
        if (!this.hasPrev) {
            return;
        }
        this.previousPage.emit(this.currentPage - 1);
    }

    next() {
        if (!this.hasNext) {
            return;
        }
        this.nextPage.emit(this.currentPage + 1);
    }
}



// WEBPACK FOOTER //
// ./src/app/pagination/pagination.component.ts